import { z } from 'zod';
import { UuidSchema } from './common';

export const RoleSchema = z.enum(['owner', 'admin', 'operator', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

export const SignupInputSchema = z.object({
  email: z.string().email().max(320),
  password: z.string().min(8).max(128),
  name: z.string().min(1).max(200).optional(),
  tenantName: z.string().min(1).max(200).optional(),
});
export type SignupInput = z.infer<typeof SignupInputSchema>;

export const LoginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof LoginInputSchema>;

export const AuthUserSchema = z.object({
  id: UuidSchema,
  email: z.string().email(),
  name: z.string().nullable(),
  /** True se user já confirmou o email via /verify-email. Default false
   * em users criados antes da feature (banner aparece na UI até verificar). */
  emailVerified: z.boolean().optional(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const AuthTenantSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  role: RoleSchema,
});
export type AuthTenant = z.infer<typeof AuthTenantSchema>;

export const AuthResponseSchema = z.object({
  user: AuthUserSchema,
  tenant: AuthTenantSchema,
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

export const MembershipSchema = z.object({
  tenantId: UuidSchema,
  tenantName: z.string(),
  role: RoleSchema,
  /** Quando true, este é o tenant pessoal do user (criado no signup). */
  isPersonal: z.boolean(),
});
export type Membership = z.infer<typeof MembershipSchema>;

export const MeResponseSchema = z.object({
  user: AuthUserSchema,
  memberships: z.array(MembershipSchema),
  currentTenant: MembershipSchema,
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const SessionListItemSchema = z.object({
  id: z.string(),
  idMasked: z.string(),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  isCurrent: z.boolean(),
});
export type SessionListItem = z.infer<typeof SessionListItemSchema>;

export const SessionsListResponseSchema = z.object({
  sessions: z.array(SessionListItemSchema),
});
export type SessionsListResponse = z.infer<typeof SessionsListResponseSchema>;

export const RequestPasswordResetInputSchema = z.object({
  email: z.string().email().max(320),
});
export type RequestPasswordResetInput = z.infer<typeof RequestPasswordResetInputSchema>;

export const ConfirmPasswordResetInputSchema = z.object({
  token: z.string().min(64).max(64),
  newPassword: z.string().min(8).max(128),
});
export type ConfirmPasswordResetInput = z.infer<typeof ConfirmPasswordResetInputSchema>;

export const VerifyEmailInputSchema = z.object({
  token: z.string().min(64).max(64),
});
export type VerifyEmailInput = z.infer<typeof VerifyEmailInputSchema>;

// ─── Workspace (tenant) operations ────────────────────────────────────────
// Operações sobre o tenant em si (criar / renomear / deletar). Membros estão
// em `tenant-members.ts`.

export const CreateTenantInputSchema = z.object({
  name: z.string().min(1).max(100),
});
export type CreateTenantInput = z.infer<typeof CreateTenantInputSchema>;

export const UpdateTenantInputSchema = z.object({
  name: z.string().min(1).max(100),
});
export type UpdateTenantInput = z.infer<typeof UpdateTenantInputSchema>;

export const DeleteTenantInputSchema = z.object({
  /** Deve bater exatamente com `tenants.name` — proteção dupla anti-delete acidental. */
  confirmName: z.string().min(1).max(100),
});
export type DeleteTenantInput = z.infer<typeof DeleteTenantInputSchema>;

export const TenantResponseSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  role: RoleSchema,
});
export type TenantResponse = z.infer<typeof TenantResponseSchema>;

export const TenantQuotaUsageSchema = z.object({
  used: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative().nullable(),
  ok: z.boolean(),
  percentUsed: z.number().int().min(0).max(100),
});
export type TenantQuotaUsage = z.infer<typeof TenantQuotaUsageSchema>;

// ─── Notifications (in-app) ──────────────────────────────────────────────
// Eventos importantes pro user (quality cair, phone pausado, campaign falhou).
// Rendered em sino no Topbar com badge de count não-lidas.

export const NotificationSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type NotificationSeverity = z.infer<typeof NotificationSeveritySchema>;

export const NotificationSchema = z.object({
  id: UuidSchema,
  type: z.string(),
  severity: NotificationSeveritySchema,
  title: z.string(),
  body: z.record(z.unknown()),
  entityType: z.string().nullable(),
  entityId: UuidSchema.nullable(),
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationsListResponseSchema = z.object({
  items: z.array(NotificationSchema),
  unreadCount: z.number().int().nonnegative(),
});
export type NotificationsListResponse = z.infer<typeof NotificationsListResponseSchema>;

// ─── Access logs (tracking de login/signup com IP+UA+geo) ────────────────

export const AccessActionSchema = z.enum(['login', 'signup', 'session_resume', 'login_fail']);
export type AccessAction = z.infer<typeof AccessActionSchema>;

export const AccessLogSchema = z.object({
  id: UuidSchema,
  userId: UuidSchema,
  userEmail: z.string().email(),
  userName: z.string().nullable(),
  action: AccessActionSchema,
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  country: z.string().nullable(),
  region: z.string().nullable(),
  city: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type AccessLog = z.infer<typeof AccessLogSchema>;

export const AccessLogsListResponseSchema = z.object({
  items: z.array(AccessLogSchema),
});
export type AccessLogsListResponse = z.infer<typeof AccessLogsListResponseSchema>;
